import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, ShieldCheck, GitBranch, PlayCircle, PauseCircle, RotateCcw,
  LifeBuoy, Search, Repeat, Undo2, FileDown, CircleCheck, CircleX, CircleSlash,
  CircleHelp, CircleAlert, Clock, Ban, Lock,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOV = `${API}/platform/migration/governance`;
const BF = `${API}/platform/migration/backfill`;
const MODULE_LABELS = { harvest: "HARVEST", haven: "HAVEN", grant_portfolio: "Grant Portfolio" };

const CHECK_STYLE = {
  satisfied: { icon: CircleCheck, cls: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  blocked: { icon: CircleX, cls: "text-rose-600", bg: "bg-rose-50 border-rose-200" },
  unavailable: { icon: CircleSlash, cls: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  expired: { icon: Clock, cls: "text-orange-600", bg: "bg-orange-50 border-orange-200" },
  revoked: { icon: CircleAlert, cls: "text-rose-700", bg: "bg-rose-50 border-rose-300" },
  not_yet_tested: { icon: CircleHelp, cls: "text-slate-500", bg: "bg-slate-50 border-slate-200" },
};

const StatusPill = ({ status, testid }) => {
  const s = CHECK_STYLE[status] || CHECK_STYLE.not_yet_tested;
  const Icon = s.icon;
  return (
    <span data-testid={testid} role="status" aria-label={status}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.bg} ${s.cls}`}>
      <Icon className="w-3 h-3" aria-hidden="true" />{status.replace(/_/g, " ")}
    </span>
  );
};

const Field = ({ label, children, testid }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-[var(--qn-foreground-subtle)]">{label}</span>
    <span className="text-sm font-medium" data-testid={testid}>{children}</span>
  </div>
);

export function MigrationCockpit() {
  const [tenantId, setTenantId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jobByModule, setJobByModule] = useState({});
  const [confirm, setConfirm] = useState(null); // {title, desc, scope, run}
  const [result, setResult] = useState(null); // {ok, title, payload}
  const [busy, setBusy] = useState(false);
  const [cutover, setCutover] = useState({});

  const loadCutover = useCallback(async (tid, mods) => {
    const entries = await Promise.all((mods || []).map(async (m) => {
      try {
        const r = await axios.get(`${GOV}/cutover/status`, { params: { module_id: m, tenant_id: tid } });
        return [m, r.data];
      } catch { return [m, null]; }
    }));
    setCutover(Object.fromEntries(entries));
  }, []);

  const load = useCallback(async (tid) => {
    if (!tid) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${GOV}/cockpit`, { params: { tenant_id: tid } });
      setData(r.data);
      loadCutover(tid, (r.data.modules || []).map((m) => m.module_id));
    } catch (e) {
      const st = e?.response?.status;
      setError(st === 403 ? "forbidden" : st === 401 ? "unauthenticated" : "load_failed");
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (tenantId) load(tenantId); }, [tenantId, load]);

  const showResult = (ok, title, payload) => {
    setResult({ ok, title, payload });
    if (ok) toast.success(`${title} — recorded`);
    else toast.error(`${title} — ${typeof payload === "string" ? payload : "failed"}`);
  };

  const runAction = async (title, fn, { refresh = true } = {}) => {
    setBusy(true);
    try {
      const res = await fn();
      showResult(true, title, res?.data ?? res ?? {});
      if (refresh) await load(tenantId);
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.response?.data || e.message;
      const label = st === 409 ? "conflict / stale revision" : st === 404 ? "not found"
        : st === 403 ? "authority required" : "failed";
      showResult(false, `${title} (${label})`, detail);
    } finally { setBusy(false); setConfirm(null); }
  };

  const ask = (title, desc, scope, run, refresh = true) => setConfirm({ title, desc, scope, run, refresh });

  // ── governed actions (every one calls a server admin API) ──────────────────
  const createJob = (m, dry) => async () => {
    const r = await axios.post(`${BF}/jobs`,
      { module_id: m, tenant_id: tenantId, batch_size: 25, dry_run: dry });
    setJobByModule((p) => ({ ...p, [m]: r.data.job_id }));
    return r;
  };
  const startBackfill = (m) => async () => {
    const j = await axios.post(`${BF}/jobs`, { module_id: m, tenant_id: tenantId, batch_size: 25, dry_run: false });
    const jid = j.data.job_id;
    setJobByModule((p) => ({ ...p, [m]: jid }));
    const lease = await axios.post(`${BF}/jobs/${jid}/lease`, { owner: "cockpit" });
    const run = await axios.post(`${BF}/jobs/${jid}/run`, { owner: "cockpit", token: lease.data.lease_token });
    return run;
  };
  const jobAction = (m, path, body) => async () => {
    const jid = jobByModule[m];
    if (!jid) throw Object.assign(new Error("no active job — Dry Run or Start first"), { response: { status: 409 } });
    return axios.post(`${BF}/jobs/${jid}/${path}`, body || {});
  };
  const exportRecon = (m) => async () => {
    const jid = jobByModule[m];
    if (!jid) throw Object.assign(new Error("no active job"), { response: { status: 409 } });
    return axios.get(`${BF}/jobs/${jid}/reconciliation`);
  };
  const rerunCanary = (m) => async () => axios.post(`${GOV}/canary/run`,
    { module_id: m, tenant_id: tenantId, task_id: `cockpit-canary:${m}`, legacy_run_id: `cockpit-legacy:${m}` });
  const cutoverDryRun = (m) => async () => axios.post(`${GOV}/cutover/dry-run`,
    { module_id: m, tenant_id: tenantId });
  const cutoverRequest = (m) => async () => {
    const res = await axios.post(`${GOV}/cutover/request`, {
      module_id: m, tenant_id: tenantId,
      rollback_plan_digest: `rollback-plan:${m}:${Date.now()}`,
      expected_policy_rev: cutover[m]?.live_policy_rev ?? 0 });
    await loadCutover(tenantId, (data.modules || []).map((x) => x.module_id));
    return res;
  };
  const cutoverApprove = (m, requestId) => async () => {
    const res = await axios.post(`${GOV}/cutover/approve`, { request_id: requestId });
    await loadCutover(tenantId, (data.modules || []).map((x) => x.module_id));
    return res;
  };
  const cutoverRevoke = (m, requestId) => async () => {
    const res = await axios.post(`${GOV}/cutover/revoke`, { request_id: requestId, reason: "cockpit stand-down" });
    await loadCutover(tenantId, (data.modules || []).map((x) => x.module_id));
    return res;
  };
  const inspectLineage = (m, sourceId) => async () => axios.get(`${BF}/lineage`,
    { params: { tenant_id: tenantId, module_id: m, source_id: sourceId } });

  // ── render states ──────────────────────────────────────────────────────────
  return (
    <div data-testid="migration-cockpit-root" className="space-y-4">
      <Card className="p-4 flex flex-wrap items-end gap-3" data-testid="cockpit-controls">
        <div className="flex flex-col gap-1">
          <Label htmlFor="cockpit-tenant" className="text-xs">Tenant scope</Label>
          <Input id="cockpit-tenant" data-testid="cockpit-tenant-input" className="w-72"
            placeholder="tenant_id (org) to inspect" value={tenantId}
            onChange={(e) => setTenantId(e.target.value.trim())} />
        </div>
        <Button variant="outline" data-testid="cockpit-refresh" disabled={!tenantId || loading}
          onClick={() => load(tenantId)} aria-label="Refresh cockpit">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
        {data && (
          <div data-testid="cockpit-authority-banner"
            className="ml-auto flex items-center gap-3 text-xs px-3 py-2 rounded-lg border bg-[var(--qn-primary-muted)]">
            <span className="inline-flex items-center gap-1 font-semibold text-[var(--qn-primary)]">
              <Lock className="w-3.5 h-3.5" />Production authority: {data.production_authority}
            </span>
            <span className="inline-flex items-center gap-1">
              <Ban className="w-3.5 h-3.5 text-rose-600" />Migrated traffic:{" "}
              {data.migrated_traffic_enabled ? "ENABLED" : "disabled (0 bps)"}
            </span>
            <span>zero-unexplained: {String(data.zero_unexplained)}</span>
          </div>
        )}
      </Card>

      {loading && <Card className="p-8 text-center text-sm text-[var(--qn-foreground-muted)]" data-testid="cockpit-loading">Loading governed migration state…</Card>}
      {!loading && error && (
        <Card className="p-8 text-center" data-testid="cockpit-error">
          <CircleAlert className="w-6 h-6 mx-auto text-rose-600 mb-2" />
          <div className="text-sm font-medium">
            {error === "forbidden" ? "Platform-administrator authority required."
              : error === "unauthenticated" ? "Sign in as a platform administrator."
                : "Could not load cockpit — partial or unavailable data."}
          </div>
        </Card>
      )}
      {!loading && !error && !tenantId && (
        <Card className="p-8 text-center text-sm text-[var(--qn-foreground-muted)]" data-testid="cockpit-empty">
          Enter a tenant scope to view governed migration readiness for HARVEST, HAVEN and Grant Portfolio.
        </Card>
      )}

      {!loading && !error && data && (data.modules || []).map((m) => (
        <Card key={m.module_id} className="p-5 space-y-4" data-testid={`module-card-${m.module_id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <GitBranch className="w-5 h-5 text-[var(--qn-primary)]" />
            <h3 className="text-lg font-semibold">{MODULE_LABELS[m.module_id] || m.module_id}</h3>
            <Badge variant="outline" data-testid={`lifecycle-${m.module_id}`}>{m.lifecycle_state}</Badge>
            <Badge className={m.cutover_ready ? "bg-emerald-600" : "bg-slate-500"}
              data-testid={`cutover-ready-${m.module_id}`}>
              {m.cutover_ready ? "cutover-ready" : "not cutover-ready"}
            </Badge>
            <Badge variant="outline" className="ml-auto" data-testid={`authority-${m.module_id}`}>
              <ShieldCheck className="w-3 h-3 mr-1" />authority: {m.production_authority}
            </Badge>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Canonical capability" testid={`capability-${m.module_id}`}>
              {m.capability?.capability_id || "—"}{" "}
              <span className="text-[10px] text-[var(--qn-foreground-subtle)]">
                ({m.capability?.digest_status}{m.capability?.capability_digest ? ` ${m.capability.capability_digest}` : ""})
              </span>
            </Field>
            <Field label="Adapter" testid={`adapter-${m.module_id}`}>{m.adapter?.available ? "available" : "unavailable"}</Field>
            <Field label="Source" testid={`source-${m.module_id}`}>{m.source_available ? "available" : "unavailable"}</Field>
            <Field label="Intake coverage" testid={`coverage-${m.module_id}`}>{Math.round((m.source_coverage?.coverage || 0) * 100)}%</Field>
            <Field label="Backfill committed" testid={`backfill-${m.module_id}`}>{m.source_coverage?.committed}/{m.source_coverage?.total_legacy}</Field>
            <Field label="Open quarantine" testid={`quarantine-${m.module_id}`}>{m.open_quarantine_count}</Field>
            <Field label="Shadow passes" testid={`shadow-${m.module_id}`}>{m.shadow_pass_history}</Field>
            <Field label="Canary" testid={`canary-${m.module_id}`}>{m.canary_state}</Field>
            <Field label="Rollback rehearsal" testid={`rollback-${m.module_id}`}>{m.rollback_rehearsal_passed ? "passed" : "not tested"}</Field>
            <Field label="Health" testid={`health-${m.module_id}`}>{m.health_state}</Field>
            <Field label="Rollout (bps)" testid={`rollout-${m.module_id}`}>{m.rollout_bps}</Field>
            <Field label="Kill switch" testid={`kill-${m.module_id}`}>{String(m.kill_switch)}</Field>
          </div>

          <div>
            <div className="text-xs font-semibold mb-2 text-[var(--qn-foreground-muted)]">Path to cutover</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(m.cutover_checklist || []).map((c) => (
                <div key={c.key} data-testid={`checklist-${m.module_id}-${c.key}`}
                  className="flex items-center justify-between gap-2 p-2 rounded-lg border bg-[var(--qn-surface)]">
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{c.label}</div>
                    {c.detail != null && <div className="text-[10px] text-[var(--qn-foreground-subtle)] truncate">{String(c.detail)}</div>}
                  </div>
                  <StatusPill status={c.status} testid={`checklist-status-${m.module_id}-${c.key}`} />
                </div>
              ))}
            </div>
          </div>

          {(m.remaining_blockers || []).length > 0 && (
            <div className="text-xs" data-testid={`blockers-${m.module_id}`}>
              <span className="font-semibold text-rose-600">Remaining blockers: </span>
              {m.remaining_blockers.join(", ")}
            </div>
          )}

          {/* Guarded cutover authorization tracker (prep only — routing stays locked at 0) */}
          <div className="rounded-lg border border-[var(--qn-border)] p-3 space-y-2"
            data-testid={`cutover-tracker-${m.module_id}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Lock className="w-4 h-4 text-[var(--qn-primary)]" />
              <span className="font-semibold">Guarded cutover authorization</span>
              <Badge variant="outline" data-testid={`cutover-control-${m.module_id}`}>
                control: {cutover[m.module_id]?.cutover_control_enabled ? "enabled" : "disabled"}
              </Badge>
              <Badge variant="outline" data-testid={`cutover-routing-${m.module_id}`}>
                production routing: {cutover[m.module_id]?.production_routing_enabled ? "ENABLED" : "disabled (0 bps)"}
              </Badge>
              {cutover[m.module_id] && (
                <span className="text-[var(--qn-foreground-subtle)]">
                  live readiness: {String(cutover[m.module_id].live_readiness_ready)}
                </span>
              )}
            </div>
            {(cutover[m.module_id]?.requests || []).slice(0, 3).map((req) => (
              <div key={req.request_id} data-testid={`cutover-request-${m.module_id}-${req.request_id}`}
                className="flex flex-wrap items-center gap-2 text-[11px] p-2 rounded border bg-[var(--qn-surface)]">
                <Badge className={req.status === "authorized_prepared" ? "bg-emerald-600" : "bg-slate-500"}>{req.status}</Badge>
                <span data-testid={`cutover-approvals-${m.module_id}-${req.request_id}`}>approvals {(req.approvals || []).length}/2</span>
                {(req.approvals || []).length > 0 && (
                  <span className="text-[var(--qn-foreground-subtle)]">
                    by {(req.approvals || []).map((a) => a.approver_id).join(", ")}
                  </span>
                )}
                <span className="text-[var(--qn-foreground-subtle)]">requester {req.requester_id}</span>
                <span className="text-[var(--qn-foreground-subtle)]">expires {req.expires_at?.slice(0, 16)}</span>
                <span className="text-[var(--qn-foreground-subtle)]">rev {req.policy_rev_at_request}</span>
                {req.status === "pending_approvals" && !req.revoked && (
                  <>
                    <Button size="sm" variant="ghost" className="h-6 px-2" data-testid={`action-${m.module_id}-cutover-approve`}
                      onClick={() => ask("Approve cutover request",
                        "Add an independent platform-admin approval. Two DISTINCT admins are required; you cannot approve your own request. Enables NO routing.",
                        { module: m.module_id, request_id: req.request_id, expected_policy_rev: req.policy_rev_at_request },
                        cutoverApprove(m.module_id, req.request_id))}>Approve</Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-600" data-testid={`action-${m.module_id}-cutover-revoke`}
                      onClick={() => ask("Revoke cutover request", "Revoke this cutover authorization request.",
                        { module: m.module_id, request_id: req.request_id }, cutoverRevoke(m.module_id, req.request_id))}>Revoke</Button>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 pt-1 border-t border-[var(--qn-border)]">
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-dry-run`}
              onClick={() => ask("Dry-run backfill", "Create a NON-committing dry-run backfill job.",
                { module: m.module_id, tenant: tenantId, effect: "no writes committed" }, createJob(m.module_id, true))}>
              <PlayCircle className="w-4 h-4 mr-1" />Dry Run
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-start`}
              onClick={() => ask("Start backfill", "Create + lease + run a committing backfill job (staging only, no traffic).",
                { module: m.module_id, tenant: tenantId, effect: "commits mappings; ZERO traffic" }, startBackfill(m.module_id))}>
              <PlayCircle className="w-4 h-4 mr-1" />Start
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-pause`}
              onClick={() => ask("Pause job", "Pause the active backfill job.",
                { module: m.module_id, job: jobByModule[m.module_id] || "(none)" }, jobAction(m.module_id, "pause"))}>
              <PauseCircle className="w-4 h-4 mr-1" />Pause
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-resume`}
              onClick={() => ask("Resume job", "Resume the paused backfill job.",
                { module: m.module_id, job: jobByModule[m.module_id] || "(none)" }, jobAction(m.module_id, "resume"))}>
              <RotateCcw className="w-4 h-4 mr-1" />Resume
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-recover`}
              onClick={() => ask("Recover abandoned work", "Recover an abandoned lease onto a new owner.",
                { module: m.module_id, job: jobByModule[m.module_id] || "(none)", expected_owner: "cockpit-recover" },
                jobAction(m.module_id, "recover-lease", { new_owner: "cockpit-recover" }))}>
              <LifeBuoy className="w-4 h-4 mr-1" />Recover
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-retry`}
              onClick={() => ask("Retry quarantined record",
                `Retry the first open quarantined source (${m.quarantine_reasons?.[0]?.source_id || "none"}).`,
                { module: m.module_id, source_id: m.quarantine_reasons?.[0]?.source_id || "(none)" },
                jobAction(m.module_id, "quarantine/retry", { source_id: m.quarantine_reasons?.[0]?.source_id || "none" }))}>
              <Repeat className="w-4 h-4 mr-1" />Retry Quarantine
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-export`}
              onClick={() => ask("Export reconciliation", "Export the reconciliation results for the active job.",
                { module: m.module_id, job: jobByModule[m.module_id] || "(none)" }, exportRecon(m.module_id))}>
              <FileDown className="w-4 h-4 mr-1" />Export Recon
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-lineage`}
              onClick={() => ask("Inspect lineage",
                `Inspect lineage for source ${m.quarantine_reasons?.[0]?.source_id || "(enter one)"}.`,
                { module: m.module_id, source_id: m.quarantine_reasons?.[0]?.source_id || "(none)" },
                inspectLineage(m.module_id, m.quarantine_reasons?.[0]?.source_id || "none"))}>
              <Search className="w-4 h-4 mr-1" />Lineage
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-canary`}
              onClick={() => ask("Rerun installation-bound canary",
                "Execute the real installation-bound Phase-5 canary (fails closed if provider/installation unavailable — never a synthetic success).",
                { module: m.module_id, tenant: tenantId }, rerunCanary(m.module_id), false)}>
              <ShieldCheck className="w-4 h-4 mr-1" />Rerun Canary
            </Button>
            <Button size="sm" variant="outline" data-testid={`action-${m.module_id}-rollback-rehearsal`}
              onClick={() => ask("Rehearse rollback", "Rehearse the rollback for the active job (no production impact).",
                { module: m.module_id, job: jobByModule[m.module_id] || "(none)" }, jobAction(m.module_id, "rollback-rehearsal"))}>
              <Undo2 className="w-4 h-4 mr-1" />Rehearse Rollback
            </Button>
            <Button size="sm" variant="secondary" data-testid={`action-${m.module_id}-cutover-dry-run`}
              onClick={() => ask("Guarded cutover — DRY RUN",
                "Pure read-only preview of what a guarded cutover would require. Changes nothing; enables NO routing (rollout stays 0). Production cutover requires a separate two-admin authorization + a later explicit execution grant.",
                { module: m.module_id, tenant: tenantId, effect: "read-only; ZERO traffic" },
                cutoverDryRun(m.module_id), false)}>
              <Lock className="w-4 h-4 mr-1" />Cutover Dry-Run
            </Button>
            <Button size="sm" variant="secondary" data-testid={`action-${m.module_id}-cutover-request`}
              onClick={() => ask("Request guarded cutover authorization",
                "Open a cutover authorization request pinned to the current live policy revision + readiness digest. Requires TWO independent platform-admin approvals; enables NO routing (rollout stays 0). Production execution requires a later separate explicit authorization.",
                { module: m.module_id, tenant: tenantId, expected_policy_rev: cutover[m.module_id]?.live_policy_rev ?? 0, effect: "creates request; ZERO traffic" },
                cutoverRequest(m.module_id), false)}>
              <ShieldCheck className="w-4 h-4 mr-1" />Request Cutover
            </Button>
          </div>
        </Card>
      ))}

      {result && (
        <Card className="p-4" data-testid="action-result">
          <div className={`text-sm font-semibold mb-2 ${result.ok ? "text-emerald-600" : "text-rose-600"}`}>
            {result.ok ? "Recorded event" : "Rejected"} — {result.title}
          </div>
          <pre className="text-[11px] bg-[var(--qn-surface)] border rounded-lg p-3 overflow-auto max-h-72"
            data-testid="action-result-payload">{JSON.stringify(result.payload, null, 2)}</pre>
        </Card>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="cockpit-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirm?.title}</DialogTitle>
            <DialogDescription>{confirm?.desc}</DialogDescription>
          </DialogHeader>
          <div className="text-xs bg-[var(--qn-surface)] border rounded-lg p-3">
            <div className="font-semibold mb-1">Scope &amp; expected revision</div>
            <pre className="overflow-auto" data-testid="cockpit-confirm-scope">{JSON.stringify(confirm?.scope, null, 2)}</pre>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} data-testid="cockpit-confirm-cancel">Cancel</Button>
            <Button disabled={busy} data-testid="cockpit-confirm-submit"
              onClick={() => runAction(confirm.title, confirm.run, { refresh: confirm.refresh !== false })}>
              {busy ? "Working…" : "Confirm (platform-admin)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
